import { z } from 'zod';

// Sub-menu carries the same permission fields as a top-level menu.
// `sub_menus` deeper than one level is undocumented → z.any() passthrough.
const SubMenuPermissionSchema = z
  .object({
    id: z.number().optional(),
    slug: z.string().optional(),
    frontend_endpoint: z.string().optional(),
    name: z.string().optional(),
    can_create: z.boolean().optional(),
    can_read: z.boolean().optional(),
    can_update: z.boolean().optional(),
    can_delete: z.boolean().optional(),
    can_approve: z.boolean().optional(),
    can_read_all: z.boolean().optional(),
    is_shown: z.boolean().optional(),
    is_approval_exist: z.boolean().optional(),
    is_read_all_exist: z.boolean().optional(),
    sub_menus: z.array(z.any()).nullable().optional(),
  })
  .passthrough();

const MenuPermissionSchema = z
  .object({
    id: z.number().optional(),
    slug: z.string().optional(),
    frontend_endpoint: z.string().optional(),
    name: z.string().optional(),
    can_create: z.boolean().optional(),
    can_read: z.boolean().optional(),
    can_update: z.boolean().optional(),
    can_delete: z.boolean().optional(),
    can_approve: z.boolean().optional(),
    can_read_all: z.boolean().optional(),
    is_shown: z.boolean().optional(),
    is_approval_exist: z.boolean().optional(),
    is_read_all_exist: z.boolean().optional(),
    sub_menus: z.array(SubMenuPermissionSchema).nullable().optional(),
  })
  .passthrough();

const CompanyFeatureSchema = z
  .object({
    id: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    company_id: z.number().optional(),
    type: z.string().optional(),
    is_active: z.boolean().optional(),
    use_fwa_drugs_pricing: z.boolean().optional(),
    use_fwa_agreed_pricing: z.boolean().optional(),
    use_fwa_medical_relevance: z.boolean().optional(),
    use_fwa_claim_benchmarking: z.boolean().optional(),
    use_fwa_eligibility: z.boolean().optional(),
    use_ai_daily_monitoring: z.boolean().nullable().optional(),
    use_initial_gop: z.boolean().nullable().optional(),
    use_final_gop: z.boolean().nullable().optional(),
    created_by: z.number().optional(),
    updated_by: z.number().optional(),
  })
  .passthrough();

// Company-scoped tamper detection setting, sourced from GET /me `tamper_setting`
// (SA-1789). Nullable — a company with no setting row has never enabled it.
const CompanyTamperSettingSchema = z
  .object({
    id: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    company_id: z.number().optional(),
    is_enabled: z.boolean().optional(),
    created_by: z.number().optional(),
    updated_by: z.number().optional(),
  })
  .passthrough();

// Company-scoped unique ticket-number detection setting, sourced from GET /me
// `ticket_number_setting` (SA-1851). Nullable — a company with no setting row
// has never enabled it.
const CompanyTicketNumberSettingSchema = z
  .object({
    id: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    company_id: z.number().optional(),
    is_enabled: z.boolean().optional(),
    created_by: z.number().optional(),
    updated_by: z.number().optional(),
  })
  .passthrough();

// Company-scoped analytics dashboard format, sourced from GET /me
// `analytic_setting` (SA-1849). Nullable — a company with no setting row has
// never had a format chosen and resolves to DEFAULT.
// `analytic_type` stays a plain string, NOT z.enum: parseMyProfile falls back to
// the raw payload on mismatch, so narrowing here would let an unknown future BE
// value degrade the whole profile parse instead of just this field. Narrowing
// happens in authUtils `getAnalyticFormat`.
const CompanyAnalyticSettingSchema = z
  .object({
    id: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    company_id: z.number().optional(),
    analytic_type: z.string().optional(),
    created_by: z.number().optional(),
    updated_by: z.number().optional(),
  })
  .passthrough();

// BE-supplied display label for a claim tab / section / field in one language
// (SA-2092). `text` may be blank — the FE then falls back to its own i18n key.
const ClaimTranslationSchema = z
  .object({
    language_code: z.string(),
    text: z.string().nullable().optional(),
  })
  .passthrough();

// BE sends the catalog ids as numbers; older payloads sent strings. Accept both
// so a type drift never fails the whole /me parse.
const ClaimCatalogIdSchema = z.union([z.number(), z.string()]);

// Field under a claim section, sourced from GET /me `claim_tab_settings[].section_settings[].field_settings`.
const ClaimFieldSettingSchema = z
  .object({
    id: z.number().optional(),
    claim_field_id: ClaimCatalogIdSchema.optional(),
    name: z.string().optional(),
    alias: z.string().optional(),
    display_order: z.number().optional(),
    is_visible: z.boolean().optional(),
    translations: z.array(ClaimTranslationSchema).nullable().optional(),
  })
  .passthrough();

// Section under a claim tab, sourced from GET /me `claim_tab_settings[].section_settings`.
const ClaimTabSectionSettingSchema = z
  .object({
    id: z.number().optional(),
    claim_section_id: ClaimCatalogIdSchema.optional(),
    name: z.string().optional(),
    alias: z.string().optional(),
    display_order: z.number().optional(),
    is_visible: z.boolean().optional(),
    translations: z.array(ClaimTranslationSchema).nullable().optional(),
    field_settings: z.array(ClaimFieldSettingSchema).nullable().optional(),
  })
  .passthrough();

// Claim tab config, sourced from GET /me `claim_tab_settings`.
const ClaimTabSettingSchema = z
  .object({
    id: z.number().optional(),
    claim_tab_id: ClaimCatalogIdSchema.optional(),
    name: z.string().optional(),
    alias: z.string().optional(),
    display_order: z.number().optional(),
    is_visible: z.boolean().optional(),
    translations: z.array(ClaimTranslationSchema).nullable().optional(),
    section_settings: z
      .array(ClaimTabSectionSettingSchema)
      .nullable()
      .optional(),
  })
  .passthrough();

// Company-scoped claim-detail layout, sourced from GET /me
// `claim_detail_setting` (SA-2092). `display_type` stays a plain string, NOT
// z.enum — same reasoning as `analytic_type`: narrowing happens in
// `useClaimDetailDisplayType`, so an unknown BE value degrades to the default
// layout instead of failing the whole profile parse.
const ClaimDetailSettingSchema = z
  .object({
    display_type: z.string().nullable().optional(),
  })
  .passthrough();

export const MyProfileSchema = z
  .object({
    id: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    email: z.string().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    company_role: z.string().optional(),
    phone: z.string().optional(),
    is_active_flag: z.boolean().optional(),
    language_preference: z.string().optional(),
    use_parallel_coverage_approval: z.boolean().nullable().optional(),
    created_by: z.number().optional(),
    updated_by: z.number().optional(),
    company_id: z.number().optional(),
    company_role_id: z.number().optional(),
    menus: z.array(MenuPermissionSchema).nullable().optional(),
    company_features: z.array(CompanyFeatureSchema).nullable().optional(),
    claim_tab_settings: z.array(ClaimTabSettingSchema).nullable().optional(),
    tamper_setting: CompanyTamperSettingSchema.nullable().optional(),
    ticket_number_setting:
      CompanyTicketNumberSettingSchema.nullable().optional(),
    analytic_setting: CompanyAnalyticSettingSchema.nullable().optional(),
    claim_detail_setting: ClaimDetailSettingSchema.nullable().optional(),
  })
  .passthrough();

const MyProfileResponseSchema = z
  .object({
    data: MyProfileSchema,
    message: z.string().optional(),
    meta: z
      .object({ http_status: z.number().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();

export const parseMyProfile = (data) => {
  const result = MyProfileResponseSchema.safeParse(data);
  if (!result.success) {
    console.warn('[Schema] MyProfile mismatch:', result.error.flatten());
    return data;
  }
  return result.data;
};
